import { useListFlowTypes } from "#hooks/adapters/useFlows";
import { useTableSortingAndPagination } from "#redux/reducers/tableStateReducer";
import { useListCustomAttributes } from "#src/components/hooks/adapters/useCustomAttributes";
import { useListEquipmentTypes } from "#src/components/hooks/adapters/useEquipment";
import { useIsFeatureAvailable } from "#src/contexts/AuthenticatedContext.helpers";
import { useStorageKey } from "#src/hooks/useStorageKey";
import { displayValueByDataType } from "#src/utils/display";
import {
  ArrayDataDisplay,
  Button,
  DataTable,
  DataTablePanel,
  HeaderType,
  ItemAction,
  Pill,
  Tooltip,
} from "@validereinc/common-components";
import {
  AssetType,
  AssetTypeType,
  CustomAttributeSchema,
  CustomAttributeType,
  SortDirection,
} from "@validereinc/domain";
import { toTitleCaseString } from "@validereinc/utilities";
import React, { useState } from "react";
import { AddCustomAttributeDialog } from "../../Dialogs/AddCustomAttributeDialog";
import { DeleteCustomAttributeDialog } from "../../Dialogs/DeleteCustomAttributeDialog";
import { EditCustomAttributeDialog } from "../../Dialogs/EditCustomAttributeDialog";

export const CustomAttributeTablePanel = ({
  assetType,
}: {
  assetType: AssetTypeType;
}) => {
  const [tableStateStorageKey] = useStorageKey(
    ["table-state"],
    ["custom-attributes", "list", assetType]
  );
  const [tableState, updateTableState] = useTableSortingAndPagination(
    {
      sortBy: "field_name",
      sortDirection: SortDirection.ASCENDING,
    },
    undefined,
    tableStateStorageKey
  );
  const [isAddCustomAttributeOpen, setIsAddCustomAttributeOpen] =
    useState(false);
  const [itemToDelete, setItemToDelete] =
    useState<CustomAttributeType | null>();
  const [itemToUpdate, setItemToUpdate] =
    useState<CustomAttributeType | null>();
  const [isCustomAttributeWriteEnabled] = useIsFeatureAvailable({
    featureFlagQuery: "core:facilities",
    permissionQuery: "custom_attribute_definition:write",
  });
  const [isCustomAttributeDeleteEnabled] = useIsFeatureAvailable({
    featureFlagQuery: "core:facilities",
    permissionQuery: "custom_attribute_definition:delete",
  });

  const params = {
    page: tableState.page,
    pageSize: tableState.pageSize,
    sortBy: tableState.sortBy,
    sortDirection: tableState.sortDirection,
    filters: { entity_type: assetType },
  };

  const equipmentTypes = useListEquipmentTypes({}).data?.data ?? [];
  const { data: flowTypes } = useListFlowTypes();
  const { data, isLoading } = useListCustomAttributes(params);
  const items = data?.data ?? [];

  const getEquipmentTypeName = (equipmentTypeId: string) => {
    const equipmentType = equipmentTypes.find((e) => e.id === equipmentTypeId);
    return equipmentType?.name;
  };
  const getFlowTypeName = (flowTypeId: string) => {
    const flowType = flowTypes?.data?.find((f) => f.id === flowTypeId);
    return flowType?.name;
  };

  const headers: Array<HeaderType<CustomAttributeType>> = [
    {
      key: CustomAttributeSchema.keyof().Enum.display_name,
      label: "Display Name",
    },
    {
      key: CustomAttributeSchema.keyof().Enum.field_name,
      label: "ID",
      isSortable: true,
    },
    {
      key: CustomAttributeSchema.keyof().Enum.description,
      label: "Description",
      renderComponent: ({ item }) => (
        <DataTable.DataRow.TextCell value={item.description} />
      ),
    },
    {
      key: CustomAttributeSchema.keyof().Enum.entity_subtypes,
      label: "Sub Types",
      renderComponent: ({ value }) => {
        const subtypeDisplayNames = Array.isArray(value)
          ? value.map((subType) => {
              switch (assetType) {
                case AssetType.EQUIPMENT:
                  return getEquipmentTypeName(subType);
                case AssetType.FLOW:
                  return getFlowTypeName(subType);
                default:
                  return value;
              }
            })
          : value;
        return (
          <ArrayDataDisplay
            value={subtypeDisplayNames as string[]}
            emptyText={"-"}
          />
        );
      },
    },
    {
      key: CustomAttributeSchema.keyof().Enum.is_required,
      label: "Is Required?",
      renderComponent: ({ value }) => displayValueByDataType(value, "boolean"),
    },
    {
      key: CustomAttributeSchema.keyof().Enum.data_type,
      label: "Data Type",
      renderComponent: ({ value }) => (
        <Pill variant="default">
          {toTitleCaseString(value as string, {
            intent: "ui",
            exceptions: ["oil-and-gas"],
          })}
        </Pill>
      ),
    },
    {
      key: CustomAttributeSchema.keyof().Enum.pick_list_values,
      label: "Pick List Values",
      renderComponent: ({ value }) =>
        displayValueByDataType(value, "multi-pick-list"),
    },
    {
      key: CustomAttributeSchema.keyof().Enum.lookup_entity_type,
      label: "Lookup Asset",
      renderComponent: ({ value }) =>
        toTitleCaseString(value as string, { intent: "ui" }),
    },
    {
      key: CustomAttributeSchema.keyof().Enum.unit,
      label: "Unit",
    },
  ];

  const getItemActions = ({
    item,
  }: {
    item: CustomAttributeType;
  }): ItemAction[] => [
    {
      label: "Edit",
      buttonProps: {
        onClick: () => setItemToUpdate(item),
        icon: "pencil-simple",
      },
    },
    ...(isCustomAttributeDeleteEnabled
      ? ([
          {
            label: "Delete",
            buttonProps: {
              onClick: () => setItemToDelete(item),
              icon: "trash",
            },
          },
        ] satisfies ItemAction[])
      : []),
  ];

  const actionRow = isCustomAttributeWriteEnabled
    ? [
        <Button
          key="add-custom-attribute"
          onClick={() => setIsAddCustomAttributeOpen(true)}
        >
          Add
        </Button>,
      ]
    : [
        <Tooltip
          key="add-custom-attribute"
          content="You do not have permissions to add new custom attributes."
        >
          <Button
            onClick={() => setIsAddCustomAttributeOpen(true)}
            disabled
            icon="lock"
          >
            Add
          </Button>
          ,
        </Tooltip>,
      ];

  return (
    <>
      <DataTablePanel
        panelProps={{ title: "Custom Attributes", actionRow }}
        dataTableProps={{
          items,
          getItemId: (item) => item.field_name,
          isLoading: isLoading,
          headers,
          getItemActions,
          onSortChange: updateTableState,
          onPaginationChange: updateTableState,
          sorting: {
            sortBy: tableState.sortBy,
            sortDirection: tableState.sortDirection,
          },
          pagination: {
            page: tableState.page,
            pageSize: tableState.pageSize,
            total: data?.total_entries,
          },
        }}
      />
      <AddCustomAttributeDialog
        isOpen={isAddCustomAttributeOpen}
        onClose={() => setIsAddCustomAttributeOpen(false)}
        assetType={assetType}
      />
      {itemToDelete && (
        <DeleteCustomAttributeDialog
          isOpen={!!itemToDelete}
          onClose={() => setItemToDelete(undefined)}
          assetType={assetType}
          itemToDelete={itemToDelete}
        />
      )}

      {itemToUpdate && (
        <EditCustomAttributeDialog
          isOpen={!!itemToUpdate}
          onClose={() => setItemToUpdate(undefined)}
          assetType={assetType}
          itemToUpdate={itemToUpdate}
        />
      )}
    </>
  );
};
